import './Drilldown.css';
import React, { useEffect, useRef } from 'react';
import { getGanWuxing, getZhiWuxing, getWuxingColor } from '../utils/wuxing-calculator';
import { getShishen, getShishenColorBySource } from '../utils/shishen-calculator';
import { getZhiBenQi, calculateDiShi, getDiShiColor } from '../utils/bazi-utils';
import { Clock, Sparkles } from 'lucide-react';

export const MONTH_NAMES = ['正月', '二月', '三月', '四月', '五月', '六月', '七月', '八月', '九月', '十月', '冬月', '腊月'];

// 干支 + 十神迷你展示（时间轴卡片内用）
const PillarMini = ({ gan, zhi, riGan }) => {
  const ganSS = getShishen(riGan, gan);
  const zhiBenQi = getZhiBenQi(zhi);
  const zhiSS = zhiBenQi ? getShishen(riGan, zhiBenQi) : '';
  return (
    <div className="dd-ganzhi">
      <div className="dd-col">
        {ganSS && <span className="dd-ss" style={{ color: getShishenColorBySource(ganSS, gan) }}>{ganSS}</span>}
        <span className="dd-char" style={{ color: getWuxingColor(getGanWuxing(gan)) }}>{gan}</span>
      </div>
      <div className="dd-col">
        {zhiSS && <span className="dd-ss" style={{ color: getShishenColorBySource(zhiSS, zhiBenQi) }}>{zhiSS}</span>}
        <span className="dd-char" style={{ color: getWuxingColor(getZhiWuxing(zhi)) }}>{zhi}</span>
      </div>
    </div>
  );
};

// 居中滚动选中项（只动本容器的横向滚动，不影响页面）
const useCenterSelected = (deps) => {
  const ref = useRef(null);
  useEffect(() => {
    const container = ref.current;
    if (!container) return;
    const el = container.querySelector('.dd-item.selected') || container.querySelector('.dd-item.current');
    if (el) {
      const target = el.offsetLeft - container.clientWidth / 2 + el.clientWidth / 2;
      container.scrollLeft = Math.max(target, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return ref;
};

const Drilldown = ({
  dayunList = [],
  qiyunAge,
  birthYear,
  riGan,
  currentYear,
  currentDayunIndex,
  selectedDayunIndex,
  onSelectDayun,
  liunianList = [],
  selectedYear,
  onSelectYear,
  liuyueList = [],
  selectedMonthIndex,
  onSelectMonth
}) => {
  const dayunRef = useCenterSelected([dayunList, selectedDayunIndex]);
  const liunianRef = useCenterSelected([liunianList, selectedYear]);
  const liuyueRef = useCenterSelected([liuyueList, selectedMonthIndex]);

  if (!dayunList.length) return null;

  const selDayun = dayunList[selectedDayunIndex] || dayunList[0];
  const dayunRange = selDayun ? `${selDayun.startYear}–${selDayun.startYear + 9}` : '';

  return (
    <div className="dd-embed" id="section-yun">
      {/* 紧凑头行：标题 + 起运信息同行（钻取选中态已由高亮项与命盘 5/6/7 列标头体现，不再放面包屑） */}
      <div className="dd-head">
        <h2>大运 · 流年 · 流月</h2>
        {qiyunAge && (
          <span className="dd-qiyun-inline">
            <Clock size={13} className="inline-icon" />
            起运 <b>{qiyunAge.years}</b> 岁 {qiyunAge.months} 个月 {qiyunAge.days} 天
            {birthYear ? `（约 ${birthYear + (qiyunAge.years || 0)} 年起运）` : ''}
          </span>
        )}
      </div>

      {/* 大运时间轴 */}
      <div className="dd-section">
        <div className="dd-rail">大运</div>
        <div className="dd-track" ref={dayunRef}>
          {dayunList.map((yun, idx) => {
            const isPreRun = !yun.gan || !yun.zhi;
            const dishi = isPreRun ? '' : calculateDiShi(riGan, yun.zhi);
            const isCurrent = idx === currentDayunIndex;
            const isSelected = idx === selectedDayunIndex;
            return (
              <button
                type="button"
                key={idx}
                className={`dd-item dd-dayun ${isSelected ? 'selected' : ''} ${isCurrent ? 'current' : ''} ${isPreRun ? 'prerun' : ''}`}
                onClick={() => { if (!isPreRun) onSelectDayun(idx); }}
                disabled={isPreRun}
              >
                <div className="dd-age">{isPreRun ? `${yun.startYear}年生` : `${yun.age}岁 · ${yun.startYear}`}</div>
                {isPreRun ? <div className="dd-prerun-label">出生 · 未起运</div> : <PillarMini gan={yun.gan} zhi={yun.zhi} riGan={riGan} />}
                {dishi && <div className="dd-dishi" style={{ color: getDiShiColor(dishi) }}>{dishi}</div>}
                {isCurrent && <span className="dd-tag">当前</span>}
              </button>
            );
          })}
        </div>
      </div>

      {/* 流年 */}
      <div className="dd-section">
        <div className="dd-rail" title={selDayun ? `${selDayun.gan}${selDayun.zhi}大运（${dayunRange}）` : ''}>
          流年
          {selDayun && <span className="dd-rail-sub">{selDayun.gan}{selDayun.zhi}运</span>}
        </div>
        <div className="dd-track" ref={liunianRef}>
          {liunianList.map((nian, idx) => {
            const dishi = calculateDiShi(riGan, nian.zhi);
            const isSelected = nian.year === selectedYear;
            const xusui = birthYear ? nian.year - birthYear + 1 : null;
            return (
              <button
                type="button"
                key={idx}
                className={`dd-item dd-liunian ${isSelected ? 'selected' : ''} ${nian.isCurrent ? 'current' : ''}`}
                onClick={() => onSelectYear(nian.year)}
              >
                <div className="dd-year">{nian.year}{xusui ? <span className="dd-xusui"> · {xusui}岁</span> : ''}</div>
                <PillarMini gan={nian.gan} zhi={nian.zhi} riGan={riGan} />
                {dishi && <div className="dd-dishi" style={{ color: getDiShiColor(dishi) }}>{dishi}</div>}
                {nian.isCurrent && <span className="dd-tag">今年</span>}
              </button>
            );
          })}
        </div>
      </div>

      {/* 流月 */}
      <div className="dd-section">
        <div className="dd-rail" title="五虎遁月诀：甲己之年丙作首，乙庚之岁戊为头，丙辛必定寻庚起，丁壬壬位顺行流，戊癸甲寅好追求">
          流月
          <span className="dd-rail-sub">{selectedYear}年</span>
          <Sparkles size={11} className="dd-note-icon" />
        </div>
        <div className="dd-track" ref={liuyueRef}>
          {liuyueList.map((yue, idx) => {
            const isSelected = idx === selectedMonthIndex;
            return (
              <button
                type="button"
                key={idx}
                className={`dd-item dd-liuyue ${isSelected ? 'selected' : ''} ${yue.isCurrent ? 'current' : ''}`}
                onClick={() => onSelectMonth(idx)}
              >
                <div className="dd-month">{MONTH_NAMES[idx]}</div>
                <PillarMini gan={yue.gan} zhi={yue.zhi} riGan={riGan} />
                {yue.isCurrent && <span className="dd-tag">本月</span>}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Drilldown;
